import { useState } from '#imports'
import { type Ref, computed, ref, watch } from 'vue'
// The level the user last dragged a sheet to, shared by every sheet: pull one up and
// the next opens up. Only a drag writes it — an automatic full height must not, or
// closing a detail would leave behind a level the user never chose.

/**
 * A phone sheet's two rest levels: `false` = its default height, `true` = full.
 * Detail content arrives full by asking for it (`autoFull`), and closing it back to
 * a list restores the user's own level instead of collapsing what they expanded.
 */
export function useSheetExpansion(
  active: Ref<boolean>,
  autoFull?: Ref<boolean>,
) {
  const preferExpanded = useState('prefer-expanded', () => false)
  // Seeded, not defaulted: `active` is false until the phone media query resolves
  // after mount, so a full arrival has to be in the SSR markup or it animates in.
  const state = ref(autoFull?.value ?? false)

  // Writing `expanded` is the drag (usePanelDrag's only write), so that is the one
  // path that publishes; the watcher below assigns `state` and stays quiet.
  const expanded = computed({
    get: () => state.value,
    set: (value) => {
      state.value = value
      if (active.value) preferExpanded.value = value
    },
  })

  watch([active, () => autoFull?.value], ([on, full]) => {
    if (on) state.value = full || preferExpanded.value
  })

  return { expanded }
}
